/**
 * CSV Export Utility
 *
 * Provides functions to transform chart data and export to CSV files
 * with proper formatting for Excel compatibility.
 */

/**
 * Transform time series chart data for CSV export
 * @param {Array} data - Array of {date, series1: value, series2: value, ...}
 * @param {string[]} seriesNames - Names of data series to include
 * @returns {Array} Transformed data with Date, series columns, and Total
 */
export function transformTimeSeriesForExport (data, seriesNames) {
	if (!data || !Array.isArray(data) || data.length === 0) {
		return []
	}

	if (!seriesNames || !Array.isArray(seriesNames) || seriesNames.length === 0) {
		return []
	}

	return data.map(row => {
		// Format the date for display
		const date = row.date ? formatDateForDisplay(row.date) : ''

		// Build the transformed row
		const transformedRow = { Date: date }

		// Add each series value
		let total = 0
		seriesNames.forEach(seriesName => {
			const rawValue = row[seriesName]
			const value = Number(rawValue) || 0
			transformedRow[seriesName] = value
			total += value
		})

		// Add the total
		transformedRow.Total = total

		return transformedRow
	})
}

/**
 * Format a date value for display in CSV
 * @param {Date|string|number} dateValue - Date to format
 * @returns {string} Formatted date string (e.g., "Jan 1, 2024")
 */
function formatDateForDisplay (dateValue) {
	try {
		const date = dateValue instanceof Date ? dateValue : new Date(dateValue)

		if (isNaN(date.getTime())) {
			return String(dateValue)
		}

		const options = { year: 'numeric', month: 'short', day: 'numeric' }
		return date.toLocaleDateString('en-GB', options)
	} catch (error) {
		return String(dateValue)
	}
}

/**
 * Escape a value for CSV (handle commas, quotes, newlines)
 * @param {*} value - Value to escape
 * @returns {string} Escaped string
 */
export function escapeCSVValue (value) {
	// Handle null/undefined
	if (value == null) {
		return ''
	}

	// Convert to string
	let stringValue = String(value)

	// Check if the value needs escaping
	const needsEscaping = stringValue.includes(',') ||
                        stringValue.includes('"') ||
                        stringValue.includes('\n') ||
                        stringValue.includes('\r')

	if (needsEscaping) {
		// Escape internal quotes by doubling them
		stringValue = stringValue.replace(/"/g, '""')
		// Wrap in quotes
		return `"${stringValue}"`
	}

	return stringValue
}

/**
 * Convert data array to CSV string
 * @param {Array} data - Array of row objects
 * @param {string[]} columns - Column names in order
 * @returns {string} CSV formatted string
 */
export function convertToCSV (data, columns) {
	if (!data || !Array.isArray(data) || data.length === 0) {
		return ''
	}

	if (!columns || !Array.isArray(columns) || columns.length === 0) {
		return ''
	}

	// Create header row
	const headerRow = columns.map(col => escapeCSVValue(col)).join(',')

	// Create data rows
	const dataRows = data.map(row => {
		return columns.map(col => {
			const value = row[col]
			return escapeCSVValue(value)
		}).join(',')
	})

	// Combine header and data rows with CRLF line endings
	return [headerRow, ...dataRows].join('\r\n')
}

/**
 * Export data to CSV file and trigger download
 * @param {Array} data - Array of row objects
 * @param {string[]} columns - Column names in order
 * @param {string} filename - Filename without extension
 */
export function exportToCSV (data, columns, filename) {
	if (!data || !Array.isArray(data) || data.length === 0) {
		console.warn('No data to export')
		return
	}

	if (!columns || !Array.isArray(columns) || columns.length === 0) {
		console.warn('No columns specified for export')
		return
	}

	if (!filename || typeof filename !== 'string') {
		filename = 'export'
	}

	try {
		// Convert data to CSV string
		const csvContent = convertToCSV(data, columns)

		if (!csvContent) {
			console.warn('Generated CSV is empty')
			return
		}

		// Add UTF-8 BOM for Excel compatibility
		const csvWithBOM = '\uFEFF' + csvContent

		// Create Blob with correct MIME type
		const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' })

		// Create temporary link and trigger download
		const link = document.createElement('a')
		const url = URL.createObjectURL(blob)

		link.setAttribute('href', url)
		link.setAttribute('download', `${filename}.csv`)
		link.style.visibility = 'hidden'

		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)

		// Clean up object URL after a short delay
		setTimeout(() => {
			URL.revokeObjectURL(url)
		}, 100)
	} catch (error) {
		console.error('Error exporting CSV:', error)
		throw error
	}
}
